import React, { useEffect, useMemo, useRef, useState } from 'react'
import { fetchText, parseSpec } from '../utils/spec'

type RapiDocExProps = {
  specUrl: string
  developerPrefix?: string // default: "[Developer]"
  collapseTagsByDefault?: boolean // default: true
  debug?: boolean // default: false
  style?: React.CSSProperties
  dark?: boolean // theme flag
  supportXTagGroups?: boolean // default: true — honor Redoc-style x-tagGroups in RapiDoc by pre-transforming
  // Styling-only: hide the built-in filter/search input in the left nav
  hideSearchFilterInNav?: boolean // default: false
}

// Helpers to support Redoc-style x-tagGroups in RapiDoc by pre-transforming the spec
type XTagGroup = { name: string; tags: string[] }

function applyXTagGroupsForRapiDoc(
  spec: any,
  options?: { generalGroupName?: string; debug?: boolean }
): any {
  const debug = options?.debug ?? false
  if (!spec || typeof spec !== 'object') return spec

  const groupsArr: XTagGroup[] = Array.isArray((spec as any)['x-tagGroups'])
    ? (spec as any)['x-tagGroups']
    : []
  if (!groupsArr.length) return spec // nothing to do

  // Collect all actual tag names from top-level tags and operations
  const topTags = Array.isArray(spec.tags) ? spec.tags : []
  const actualTagNamesSet = new Set<string>()
  const actualTagNames: string[] = []

  for (const t of topTags) {
    if (t && typeof t.name === 'string') {
      const n = String(t.name)
      if (!actualTagNamesSet.has(n)) {
        actualTagNamesSet.add(n)
        actualTagNames.push(n)
      }
    }
  }

  const paths = spec.paths || {}
  for (const p of Object.keys(paths)) {
    const item = paths[p]
    if (!item || typeof item !== 'object') continue
    for (const method of Object.keys(item)) {
      const op = item[method]
      if (!op || !Array.isArray(op.tags)) continue
      for (const tag of op.tags) {
        if (typeof tag !== 'string') continue
        if (!actualTagNamesSet.has(tag)) {
          actualTagNamesSet.add(tag)
          actualTagNames.push(tag)
        }
      }
    }
  }

  // Map the authored group tag labels to actual tag names using exact and lightly-normalized matching
  const tagToGroup = new Map<string, string>() // actualTagName -> groupName
  const seenGroupNames: string[] = []
  for (const g of groupsArr) {
    if (!g) continue
    const gName = g.name
    if (!seenGroupNames.includes(gName)) seenGroupNames.push(gName)
    const authoredTags: string[] = Array.isArray(g.tags) ? g.tags : []
    const authoredSet = new Set(authoredTags.map((t) => String(t)))
    const authoredNormSet = new Set(
      authoredTags.map((t) => String(t).trim().toLowerCase().replace(/\s+/g, ' '))
    )

    for (const actual of actualTagNames) {
      if (tagToGroup.has(actual)) continue
      const trimmed = String(actual).trim()
      // 1) Prefer exact authored match (case-sensitive)
      if (authoredSet.has(trimmed)) {
        tagToGroup.set(actual, gName)
        continue
      }
      // 2) Fallback to light normalized match (case/space only)
      const normActual = trimmed.toLowerCase().replace(/\s+/g, ' ')
      if (authoredNormSet.has(normActual)) {
        tagToGroup.set(actual, gName)
      }
    }
  }

  // Determine which tags are unassigned to any group (for General)
  const unassigned = actualTagNames.filter((t) => !tagToGroup.has(t))

    // Expose grouping metadata for the nav DOM enhancer
  ;(spec as any)['x-ns-tagGroupMap'] = Object.fromEntries(tagToGroup.entries())
  ;(spec as any)['x-ns-tagGroupOrder'] = [...seenGroupNames]

  if (debug) {
    // eslint-disable-next-line no-console
    console.debug('RapiDocEx: x-tagGroups processed. Groups:', seenGroupNames, 'Unassigned:', unassigned.length)
  }
  return spec
}

export default function RapiDocEx(
  {
    specUrl,
    developerPrefix = '[Developer]',
    collapseTagsByDefault = true,
    debug = false,
    style,
    dark = false,
    supportXTagGroups = true,
    hideSearchFilterInNav = false,
  }: RapiDocExProps) {
  const elRef = useRef<any>(null)
  const [fallback, setFallback] = useState(false)
  const containerStyle = useMemo<React.CSSProperties>(
    () => ({ height: 'calc(100vh - 230px)', minHeight: '650px', width: '100%', border: '1px solid var(--border)', borderRadius: 12, ...style }),
    [style]
  )

  // Derive colors directly from the theme flag (matching the site tokens). We avoid reading
  // getComputedStyle here because on a theme switch RapiDoc remounts before the data-theme
  // attribute settles, which would pin the wrong (light) palette onto the explorer.
  const cssVars = useMemo(() => ({
    panel: dark ? '#141d17' : '#ffffff',
    surface: dark ? '#0d130f' : '#eef2f7',
    brand: dark ? '#2f7d47' : '#2a5932',
    border: dark ? '#28332b' : '#e4e4e4',
    text: dark ? '#e5eee8' : '#1a1a1a',
    link: dark ? '#74d68c' : '#2a5932',
  }), [dark])

  const FONT_STACK = "Urbanist, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
  const MONO_STACK = "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace"

  // Ensure the custom element is defined before assigning properties
  useEffect(() => {
    let canceled = false
    customElements.whenDefined('rapi-doc').then(async () => {
      if (canceled) return
      try {
        const text = await fetchText(specUrl)
        const parsed = await parseSpec(text)
        // Apply optional x-tagGroups support
        const withGroups = supportXTagGroups
          ? applyXTagGroupsForRapiDoc(parsed, {
            generalGroupName: '— General —',
            debug,
          })
          : parsed
        const el = elRef.current
        if (!el) return

        // Assign attributes and properties
        el.setAttribute('render-style', 'focused')
        el.setAttribute('theme', dark ? 'dark' : 'light')
        el.setAttribute('show-header', 'false')
        // Colors - white nav, grey content body (mirrors the docs layout)
        el.setAttribute('primary-color', cssVars.brand)
        el.setAttribute('bg-color', cssVars.surface)
        el.setAttribute('nav-bg-color', cssVars.panel)
        el.setAttribute('text-color', cssVars.text)
        el.setAttribute('header-color', cssVars.text)
        el.setAttribute('nav-text-color', cssVars.text)
        el.setAttribute('nav-hover-bg-color', dark ? 'rgba(116, 214, 140, 0.10)' : 'rgba(42, 89, 50, 0.07)')
        el.setAttribute('nav-hover-text-color', cssVars.text)
        el.setAttribute('nav-accent-color', cssVars.brand)
        // Typography - match the docs font
        el.setAttribute('regular-font', FONT_STACK)
        el.setAttribute('mono-font', MONO_STACK)
        el.setAttribute('font-size', 'default')
        // Schemas as tables with descriptions expanded (closer to our reference tables)
        el.setAttribute('schema-style', 'table')
        el.setAttribute('schema-description-expanded', 'true')
        el.setAttribute('default-schema-tab', 'schema')
        // Try-it console (with auth + server selection)
        el.setAttribute('allow-try', 'true')
        el.setAttribute('allow-authentication', 'true')
        el.setAttribute('allow-server-selection', 'true')
        el.setAttribute('show-curl-before-try', 'true')
        el.setAttribute('response-area-height', '360px')
        el.setAttribute('nav-item-spacing', 'relaxed')
        el.setAttribute('use-path-in-nav-bar', 'false')
        el.setAttribute('show-method-in-nav-bar', 'true')
        el.setAttribute('spec-url', specUrl)
        el.setAttribute('allow-spec-url-load', 'false')
        el.setAttribute('allow-spec-file-load', 'false')
        el.style.height = containerStyle.height as string
        el.style.width = containerStyle.width as string
        el.style.border = containerStyle.border as string
        el.style.borderRadius = containerStyle.borderRadius as string

        // Important: pass the spec object via property
        try {
          // some RapiDoc versions accept direct property, others prefer loadSpec()
          if (typeof el.loadSpec === 'function') {
            el.loadSpec(withGroups)
          } else {
            el.spec = withGroups
          }
          // Expose grouping metadata on the element for nav enhancements
          try {
            const gm = (withGroups as any)['x-ns-tagGroupMap']
            const go = (withGroups as any)['x-ns-tagGroupOrder']
            if (gm) (el as any).__nsTagGroupMap = gm
            if (go) (el as any).__nsTagGroupOrder = go
          } catch {
          }
        } catch (e) {
          if (debug) console.warn('RapiDocEx: error assigning spec, will fallback', e)
          setFallback(true)
          return
        }

        let loaded = false
        const onLoaded = () => {
          loaded = true
          try {
            // Styling-only: optionally hide the filter/search bar in the left nav
            if (hideSearchFilterInNav) {
              const root: ShadowRoot | undefined = el.shadowRoot
              if (root && !root.getElementById('ns-rapidoc-hide-search')) {
                const styleEl = document.createElement('style')
                styleEl.id = 'ns-rapidoc-hide-search'
                // Hide the entire search area in the left navbar
                styleEl.textContent = `nav[part="section-navbar"] [part="section-navbar-search"]{display:none !important;}`
                root.appendChild(styleEl)
              }
            }
          } catch {
          }
          if (collapseTagsByDefault) {
            try {
              const root: ShadowRoot | undefined = el.shadowRoot
              if (!root) return

              //

              // Fallback mechanism: if RapiDoc does not render collapsible structures
              // (no <details> and no aria-expanded toggles), we wrap each tag section's
              // endpoints into a collapsible container that we control. This relies on
              // detecting elements marked with a data-tag attribute and grouping their
              // following siblings until the next data-tag element.
              const ensureCustomCollapsibleGroups = () => {
                // noinspection CssInvalidHtmlTagReference
                const navRoot =
                  (root.querySelector('[part="nav-bar"]') as HTMLElement) ||
                  (root.querySelector('rapi-nav') as HTMLElement) ||
                  (root.querySelector('nav[role="navigation"]') as HTMLElement) ||
                  (root.querySelector('nav') as HTMLElement) ||
                  undefined
                if (!navRoot) return false
                // Guard: if we already built custom collapsible on this nav, skip
                if ((navRoot as HTMLElement).getAttribute('data-ns-collapsible') === '1') return true

                // Ensure a single delegated toggle handler exists so that even if RapiDoc
                // re-renders parts of the nav (detaching individual listeners), our
                // expand/collapse still works. Also adds basic keyboard support.
                const ensureDelegatedToggleHandlers = () => {
                  const hostAny = root as any
                  if (hostAny.__nsDelegatedToggleInstalled) return
                  hostAny.__nsDelegatedToggleInstalled = true

                  const toggleForHeader = (headerEl: HTMLElement) => {
                    // Find the immediate next .ns-tag-group sibling (skip non-element nodes and unrelated siblings)
                    let sib = headerEl.nextElementSibling as HTMLElement | null
                    while (sib && !sib.classList.contains('ns-tag-group')) {
                      sib = sib.nextElementSibling as HTMLElement | null
                    }
                    const group = sib
                    if (!group) return false
                    const nextExpanded = group.getAttribute('data-collapsed') !== 'false'
                    group.setAttribute('data-collapsed', nextExpanded ? 'false' : 'true')

                    // Also sync any RapiDoc variant classes and inline styles that control height/visibility
                    const block = headerEl.closest('.nav-bar-tag-and-paths') as HTMLElement | null
                    if (block) {
                      if (nextExpanded) block.classList.remove('collapsed')
                      else block.classList.add('collapsed')
                    }
                    // Reflect state for accessibility; some CSS may rely on aria-expanded
                    headerEl.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false')
                    // Clear/force height styles on inner container(s)
                    const pathContainers = Array.from(group.querySelectorAll('.nav-bar-paths-under-tag')) as HTMLElement[]
                    pathContainers.forEach((pc) => {
                      if (nextExpanded) {
                        pc.style.setProperty('display', 'block', 'important')
                        pc.style.setProperty('height', 'auto', 'important')
                        pc.style.setProperty('max-height', 'none', 'important')
                        pc.style.setProperty('overflow', 'visible', 'important')
                        pc.style.setProperty('opacity', '1', 'important')
                        pc.style.setProperty('visibility', 'visible', 'important')
                      } else {
                        pc.style.setProperty('display', 'block', 'important')
                        pc.style.setProperty('height', '0', 'important')
                        pc.style.setProperty('max-height', '0px', 'important')
                        pc.style.setProperty('overflow', 'hidden', 'important')
                        pc.style.setProperty('opacity', '0', 'important')
                        pc.style.setProperty('visibility', 'hidden', 'important')
                      }
                    })
                    return true
                  }

                  // Mouse/touch: capture to get events even if RapiDoc stops propagation
                  const onClick = (e: Event) => {
                    const t = e.target as HTMLElement | null
                    if (!t) return
                    // Ignore clicks intended for navigation
                    if (t.closest('[data-action="navigate"]')) return
                    // Ignore clicks anywhere inside the content group (only header should toggle)
                    if (t.closest('.ns-tag-group')) return
                    const header = t.closest('.ns-tag-header') as HTMLElement | null
                    if (!header || !navRoot.contains(header)) return
                    // Prevent default navigation if any to avoid jumping
                    e.preventDefault?.()
                    toggleForHeader(header)
                  }
                  root.addEventListener('click', onClick, true)

                  // Keyboard: Enter/Space on a focused header
                  const onKeyDown = (e: Event) => {
                    const ke = e as KeyboardEvent
                    const t = ke.target as HTMLElement | null
                    if (!t || !t.classList || !t.classList.contains('ns-tag-header')) return
                    if (ke.key === 'Enter' || ke.key === ' ') {
                      ke.preventDefault()
                      toggleForHeader(t)
                    }
                  }
                  root.addEventListener('keydown', onKeyDown, true)
                }

                const tagHeaders = Array.from(navRoot.querySelectorAll('[data-tag]')) as HTMLElement[]
                if (debug) {
                  console.log('RapiDocEx: ensureCustomCollapsibleGroups navRoot=', navRoot?.tagName)
                  console.log('RapiDocEx: [data-tag] headers found =', tagHeaders.length)
                }
                // If no explicit [data-tag] headers exist, try a heuristic-based grouping
                if (tagHeaders.length === 0) {
                  // Detect RapiDoc nav variant that uses `.nav-bar-tag-and-paths` blocks per tag
                  const variantContainers = Array.from(
                    navRoot.querySelectorAll('nav[part="section-navbar-scroll"] .nav-bar-tag-and-paths, .nav-bar-tag-and-paths')
                  ) as HTMLElement[]
                  if (variantContainers.length > 0) {
                    if (debug) console.log('RapiDocEx: found nav variant .nav-bar-tag-and-paths count =', variantContainers.length)
                    // Inject minimal CSS here once
                    if (!root.getElementById('ns-rapidoc-collapsible-css')) {
                      const styleEl = document.createElement('style')
                      styleEl.id = 'ns-rapidoc-collapsible-css'
                      styleEl.textContent = `
                        .ns-tag-group[data-collapsed="true"] { display: none; }
                        .ns-tag-header { cursor: pointer; user-select: none; }
                        /* Ensure inner RapiDoc containers are visible when expanded, regardless of inline styles */
                        .ns-tag-group[data-collapsed="false"] .nav-bar-paths-under-tag {
                          display: block !important;
                          height: auto !important;
                          max-height: none !important;
                          overflow: visible !important;
                          opacity: 1 !important;
                          visibility: visible !important;
                        }
                        .ns-tag-group[data-collapsed="true"] .nav-bar-paths-under-tag {
                          display: block !important;
                          height: 0 !important;
                          max-height: 0 !important;
                          overflow: hidden !important;
                          opacity: 0 !important;
                          visibility: hidden !important;
                        }
                      `
                      root.appendChild(styleEl)
                    }

                    variantContainers.forEach((container) => {
                      // Idempotent: skip if already processed
                      if ((container as HTMLElement).dataset.nsProcessed === '1') return
                        ;
                      (container as HTMLElement).dataset.nsProcessed = '1'

                      // Heuristic to pick a header child: the first direct child whose text does not start with '/'
                      const children = Array.from(container.children) as HTMLElement[]
                      let headerEl: HTMLElement | undefined
                      for (const c of children) {
                        const txt = (c.textContent || '').trim()
                        if (txt && !txt.startsWith('/')) {
                          headerEl = c
                          break
                        }
                      }
                      // If we still didn't find a header child, synthesize one at the top
                      if (!headerEl) {
                        const synth = document.createElement('div')
                        synth.className = 'ns-tag-header'
                        // Try to derive a reasonable title from existing text
                        const raw = (container.textContent || '').split('\n').map((s) => s.trim()).filter(Boolean)
                        synth.textContent = raw.find((t) => t && !t.startsWith('/')) || 'Section'
                        container.insertBefore(synth, container.firstChild)
                        headerEl = synth
                      }

                      if (!headerEl) return
                      if (!headerEl.classList.contains('ns-tag-header')) headerEl.classList.add('ns-tag-header')
                      const headerText = (headerEl.textContent || '').trim()
                      if (headerText && !headerEl.hasAttribute('data-tag')) {
                        headerEl.setAttribute('data-tag', headerText)
                      }
                      // Accessibility and focus
                      headerEl.setAttribute('role', 'button')
                      if (!headerEl.hasAttribute('tabindex')) headerEl.setAttribute('tabindex', '0')

                      // Group all siblings after the header into a collapsible wrapper
                      const group = document.createElement('div')
                      group.className = 'ns-tag-group'
                      group.setAttribute('data-collapsed', 'true')
                      // Insert the group immediately after the header, then move all other children into it
                      headerEl.parentElement?.insertBefore(group, headerEl.nextSibling)
                      const childrenToMove = Array.from(container.children)
                        .filter((c) => c !== headerEl && c !== group)
                      childrenToMove.forEach((n) => group.appendChild(n))

                      // Note: we rely on delegated handlers for robustness, so no per-node listener here
                    })

                    // Insert group separators for this variant now that headers were synthesized
                    try {
                      const hostEl2 = el as any
                      const groupMap2: Record<string, string> | undefined = hostEl2.__nsTagGroupMap || hostEl2.spec?.['x-ns-tagGroupMap']
                      const groupOrder2: string[] | undefined = hostEl2.__nsTagGroupOrder || hostEl2.spec?.['x-ns-tagGroupOrder']
                      if (groupMap2 && groupOrder2) {
                        const clean2 = (s: string) => {
                          try {
                            let v = String(s || '')
                            v = v.toLowerCase()
                            v = v.replace(/\s*[(-]\s*\d+\s*[)]?\s*$/g, '')
                            v = v.replace(/[^a-z0-9\-\s]+/g, '')
                            v = v.replace(/\s+/g, ' ').trim()
                            return v
                          } catch {
                            return s
                          }
                        }
                        if (!root.getElementById('ns-rapidoc-collapsible-css')) {
                          const styleEl = document.createElement('style')
                          styleEl.id = 'ns-rapidoc-collapsible-css'
                          styleEl.textContent = `.ns-tag-separator{pointer-events:none;font-weight:600;padding:6px 8px;margin:10px 0 4px;color: var(--text);opacity:0.85;}`
                          root.appendChild(styleEl)
                        }
                        const normGroupMap2 = new Map<string, string>()
                        Object.entries(groupMap2).forEach(([tagName, grp]) => normGroupMap2.set(clean2(String(tagName)), String(grp)))
                        const headers2 = Array.from(navRoot.querySelectorAll('[data-tag]')) as HTMLElement[]
                        const items2 = headers2.map((h) => {
                          const rawAttr = h.getAttribute('data-tag')
                          const rawText = (h.textContent || '').trim()
                          const raw = (rawAttr && rawAttr.trim()) || rawText
                          return { el: h, norm: clean2(raw) }
                        })
                        const existing2 = new Set<string>(
                          Array.from(navRoot.querySelectorAll('.ns-tag-separator')).map((n) => (n as HTMLElement).textContent || '')
                        )
                        let inserted2 = 0
                        for (const gName of groupOrder2) {
                          if (existing2.has(gName)) continue
                          let idx = items2.findIndex((it) => normGroupMap2.get(it.norm) === gName)
                          if (idx < 0) {
                            // relaxed: try contains
                            const tagsFor = Object.entries(groupMap2)
                              .filter(([_, grp]) => grp === gName)
                              .map(([t]) => clean2(t))
                            idx = items2.findIndex((it) => tagsFor.some((tc) => it.norm === tc || it.norm.includes(tc) || tc.includes(it.norm)))
                          }
                          if (idx >= 0) {
                            const sep = document.createElement('div')
                            sep.className = 'ns-tag-separator'
                            sep.setAttribute('aria-hidden', 'true')
                            sep.setAttribute('role', 'presentation')
                            sep.textContent = gName
                            const ref = items2[idx].el
                            ref.parentElement?.insertBefore(sep, ref)
                            inserted2++
                          }
                        }
                        if (debug) console.log('RapiDocEx: inserted separators after variant synth =', inserted2)

                        // Deterministic ordering for this variant: [unassigned] + groups in authored order
                        const scrollContainer = (navRoot.querySelector('nav[part="section-navbar-scroll"]') as HTMLElement) || navRoot

                        // Build a stable list of all tag blocks with their headers
                        const allBlocks = Array.from(scrollContainer.querySelectorAll('.nav-bar-tag-and-paths')) as HTMLElement[]
                        const blockInfo = allBlocks.map((blk) => {
                          const h = blk.querySelector('[data-tag]') as HTMLElement | null
                          const rawAttr = h?.getAttribute('data-tag') || ''
                          const rawText = (h?.textContent || '').trim()
                          const raw = (rawAttr && rawAttr.trim()) || rawText
                          const norm = clean2(raw)
                          const grp = normGroupMap2.get(norm)
                          return { blk, header: h, raw, norm, grp }
                        })

                        // Desired order list
                        const desired: HTMLElement[] = []

                        // 1) Unassigned first, keep current relative order
                        blockInfo
                          .filter((b) => !b.grp && b.header)
                          .forEach((b) => desired.push(b.blk))

                        // Helper map from cleaned header to block
                        const byClean = new Map<string, HTMLElement>()
                        blockInfo.forEach((b) => {
                          if (b.header) byClean.set(b.norm, b.blk)
                        })

                        // 2) For each authored group, push members in authored order
                        for (const gName of groupOrder2) {
                          const tagsFor = Object.entries(groupMap2)
                            .filter(([_, grp]) => grp === gName)
                            .map(([t]) => t)
                          const tagsClean = tagsFor.map((t) => clean2(t))
                          tagsClean.forEach((tc) => {
                            const blk = byClean.get(tc)
                            if (blk && !desired.includes(blk)) desired.push(blk)
                          })
                          // Fallback: any remaining blocks already mapped to this group, keep current order
                          blockInfo
                            .filter((b) => b.grp === gName && !desired.includes(b.blk))
                            .forEach((b) => desired.push(b.blk))
                        }

                        // Apply ordering idempotently by appending in the desired sequence
                        desired.forEach((blk) => {
                          if (blk.parentElement === scrollContainer) scrollContainer.appendChild(blk)
                        })

                        // Remove existing separators and re-insert before the first block of each group
                        Array.from(scrollContainer.querySelectorAll('.ns-tag-separator')).forEach((n) => n.remove())

                        for (const gName of groupOrder2) {
                          const firstForGroup = desired.find((blk) => {
                            const h = blk.querySelector('[data-tag]') as HTMLElement | null
                            const nm = clean2((h?.getAttribute('data-tag') || h?.textContent || '').trim())
                            return normGroupMap2.get(nm) === gName
                          })
                          if (firstForGroup) {
                            const sep = document.createElement('div')
                            sep.className = 'ns-tag-separator'
                            sep.setAttribute('aria-hidden', 'true')
                            sep.setAttribute('role', 'presentation')
                            sep.textContent = gName
                            scrollContainer.insertBefore(sep, firstForGroup)
                          }
                        }

                        // --- Styling-only enhancements -----------------------------------------
                        // 1) Inject a scoped stylesheet to make RapiDoc's left nav look like our docs menu
                        try {
                          const styleId = 'ns-rapidoc-qplay-styles'
                          let styleEl = root.getElementById(styleId) as HTMLStyleElement | null
                          if (!styleEl) {
                            styleEl = document.createElement('style')
                            styleEl.id = styleId
                            root.appendChild(styleEl)
                          }
                          // Read brand/border colors that we already synchronized on the host element
                          const brand = (document.documentElement && getComputedStyle(document.documentElement).getPropertyValue('--brand').trim()) || '#2a5932'
                          const border = (document.documentElement && getComputedStyle(document.documentElement).getPropertyValue('--border').trim()) || '#e4e4e4'
                          const text = (document.documentElement && getComputedStyle(document.documentElement).getPropertyValue('--text').trim()) || '#1a1a1a'
                          const itemHoverBg = 'rgba(42, 89, 50, 0.06)'
                          const leftAccent = brand
                          styleEl.textContent = `
                            /* Base typography alignment */
                            :host, .nav-bar, .nav-scroll, .nav-bar-tag-and-paths, .ns-tag-separator, .ns-tag-header {
                              font-family: inherit; /* Urbanist from site theme */
                            }

                            /* Group titles — align with top menu items (Q-Play API, API Servers, Authentication) */
                            .ns-tag-separator{
                              pointer-events: none;
                              color: ${text};
                              /* Top menu look: compact, uppercase label with tracking */
                              font-weight: 700;
                              font-size: 12px;
                              line-height: 1.2;
                              text-transform: uppercase;
                              letter-spacing: 0.06em;
                              /* Spacing to visually align with tag headers/items */
                              margin: 14px 6px 6px;
                              padding: 8px 10px 0 14px;
                              /* Use a subtle separator like top menu underline rather than a full border block */
                              border-top: none;
                              /* Match top menu item tone in dark mode */
                              opacity: 0.85;
                            }

                            /* Tag headers — keep as navigable items under each group */
                            .nav-bar-tag-and-paths .ns-tag-header{
                              cursor: pointer;
                              user-select: none;
                              font-size: 14px;
                              line-height: 1.35;
                              font-weight: 600;
                              color: ${text};
                              padding: 6px 10px 6px 14px;
                              border-radius: 8px;
                              margin: 2px 6px;
                              position: relative;
                              background: transparent;
                            }
                            .nav-bar-tag-and-paths .ns-tag-header:hover{ background: ${itemHoverBg}; }
                            .nav-bar-tag-and-paths .ns-tag-header:focus-visible{
                              outline: none;
                              box-shadow: 0 0 0 2px ${leftAccent}33 inset, 0 0 0 1px ${leftAccent}66 inset;
                            }

                            /* HTTP method badge styling in left nav */
                            [part="nav-bar"] a .http-verb,
                            .nav-bar a .http-verb,
                            [part="nav-bar"] [data-http-method],
                            .nav-bar [data-http-method],
                            [part="nav-bar"] [part="http-method"],
                            .nav-bar [part="http-method"],
                            /* Support RapiDoc builds that use .nav-method */
                            [part="section-navbar"] .nav-method,
                            .nav-bar .nav-method{
                              display: inline-flex;
                              align-items: center;
                              justify-content: center;
                              width: var(--api-badge-width, 50px) !important; /* unified width for all methods; overridable */
                              height: 18px;
                              padding: 0 6px;
                              margin-right: 8px;
                              border-radius: var(--api-badge-radius, 2px) !important;
                              font-weight: 700;
                              font-size: 11px;
                              line-height: 1;
                              letter-spacing: 0.03em;
                              color: var(--api-badge-text, #ffffff) !important;
                              /* Neutral default; verb-specific rules below will override */
                              background: var(--api-badge-default, #6b7280) !important;
                              text-transform: uppercase;
                              box-sizing: border-box;
                              flex: 0 0 auto;
                            }

                            /* Per-verb colors (support data attr, class, and case variants) */
                            /* GET = green */
                            [part="nav-bar"] [data-http-method="get"],
                            [part="nav-bar"] [data-http-method="GET"],
                            .nav-bar [data-http-method="get"],
                            .nav-bar [data-http-method="GET"],
                            [part="nav-bar"] .http-verb.get,
                            [part="nav-bar"] .http-verb.GET,
                            .nav-bar .http-verb.get,
                            .nav-bar .http-verb.GET,
                            /* Support alt selector */
                            [part="section-navbar"] .nav-method.get,
                            .nav-bar .nav-method.get { background: var(--api-badge-get, #22c55e) !important; }

                            /* POST = blue */
                            [part="nav-bar"] [data-http-method="post"],
                            [part="nav-bar"] [data-http-method="POST"],
                            .nav-bar [data-http-method="post"],
                            .nav-bar [data-http-method="POST"],
                            [part="nav-bar"] .http-verb.post,
                            [part="nav-bar"] .http-verb.POST,
                            .nav-bar .http-verb.post,
                            .nav-bar .http-verb.POST,
                            /* Support alt selector */
                            [part="section-navbar"] .nav-method.post,
                            .nav-bar .nav-method.post { background: var(--api-badge-post, #3b82f6) !important; }

                            /* PUT = purple */
                            [part="nav-bar"] [data-http-method="put"],
                            [part="nav-bar"] [data-http-method="PUT"],
                            .nav-bar [data-http-method="put"],
                            .nav-bar [data-http-method="PUT"],
                            [part="nav-bar"] .http-verb.put,
                            [part="nav-bar"] .http-verb.PUT,
                            .nav-bar .http-verb.put,
                            .nav-bar .http-verb.PUT,
                            /* Support alt selector */
                            [part="section-navbar"] .nav-method.put,
                            .nav-bar .nav-method.put { background: var(--api-badge-put, #a855f7) !important; }

                            /* PATCH = teal */
                            [part="nav-bar"] [data-http-method="patch"],
                            [part="nav-bar"] [data-http-method="PATCH"],
                            .nav-bar [data-http-method="patch"],
                            .nav-bar [data-http-method="PATCH"],
                            [part="nav-bar"] .http-verb.patch,
                            [part="nav-bar"] .http-verb.PATCH,
                            .nav-bar .http-verb.patch,
                            .nav-bar .http-verb.PATCH,
                            /* Support alt selector */
                            [part="section-navbar"] .nav-method.patch,
                            .nav-bar .nav-method.patch { background: var(--api-badge-patch, #14b8a6) !important; }

                            /* DELETE = red */
                            [part="nav-bar"] [data-http-method="delete"],
                            [part="nav-bar"] [data-http-method="DELETE"],
                            .nav-bar [data-http-method="delete"],
                            .nav-bar [data-http-method="DELETE"],
                            [part="nav-bar"] .http-verb.delete,
                            [part="nav-bar"] .http-verb.DELETE,
                            .nav-bar .http-verb.delete,
                            .nav-bar .http-verb.DELETE,
                            /* Support alt selector */
                            [part="section-navbar"] .nav-method.delete,
                            .nav-bar .nav-method.delete { background: var(--api-badge-delete, #ef4444) !important; }

                            /* HEAD = gray */
                            [part="nav-bar"] [data-http-method="head"],
                            [part="nav-bar"] [data-http-method="HEAD"],
                            .nav-bar [data-http-method="head"],
                            .nav-bar [data-http-method="HEAD"],
                            [part="nav-bar"] .http-verb.head,
                            [part="nav-bar"] .http-verb.HEAD,
                            .nav-bar .http-verb.head,
                            .nav-bar .http-verb.HEAD,
                            /* Support alt selector */
                            [part="section-navbar"] .nav-method.head,
                            .nav-bar .nav-method.head { background: var(--api-badge-head, #9ca3af) !important; }

                            /* OPTIONS = gray */
                            [part="nav-bar"] [data-http-method="options"],
                            [part="nav-bar"] [data-http-method="OPTIONS"],
                            .nav-bar [data-http-method="options"],
                            .nav-bar [data-http-method="OPTIONS"],
                            [part="nav-bar"] .http-verb.options,
                            [part="nav-bar"] .http-verb.OPTIONS,
                            .nav-bar .http-verb.options,
                            .nav-bar .http-verb.OPTIONS,
                            /* Support alt selector */
                            [part="section-navbar"] .nav-method.options,
                            .nav-bar .nav-method.options { background: var(--api-badge-options, #9ca3af) !important; }

                            /* TRACE = amber */
                            [part="nav-bar"] [data-http-method="trace"],
                            [part="nav-bar"] [data-http-method="TRACE"],
                            .nav-bar [data-http-method="trace"],
                            .nav-bar [data-http-method="TRACE"],
                            [part="nav-bar"] .http-verb.trace,
                            [part="nav-bar"] .http-verb.TRACE,
                            .nav-bar .http-verb.trace,
                            .nav-bar .http-verb.TRACE,
                            /* Support alt selector */
                            [part="section-navbar"] .nav-method.trace,
                            .nav-bar .nav-method.trace { background: var(--api-badge-trace, #f59e0b) !important; }

                            /* Active tag marker (explicit class) */
                            .nav-bar-tag-and-paths .ns-tag-header.ns-active-tag::before{
                              content: '';
                              position: absolute;
                              left: 0;
                              top: 6px;
                              bottom: 6px;
                              width: 3px;
                              background: ${leftAccent};
                              border-radius: 3px;
                            }

                            /* Endpoint list indentation & subtle divider to mirror docs sidebar nesting */
                            .nav-bar-tag-and-paths .ns-tag-group .nav-bar-paths-under-tag{
                              padding-left: 12px;
                              border-left: 1px solid ${border};
                              margin-left: 6px;
                            }

                            /* Slightly tighter spacing between consecutive tag blocks */
                            nav[part="section-navbar-scroll"] .nav-bar-tag-and-paths{ margin-bottom: 2px; }

                            /* Chevron/expand icons visibility */
                            .nav-bar-tag-and-paths .ns-tag-header svg{ opacity: 0.75; }

                            /* Ensure expanded/collapsed endpoint visibility remains consistent */
                            .ns-tag-group[data-collapsed="false"] .nav-bar-paths-under-tag{
                              display: block !important; height: auto !important; max-height: none !important;
                              overflow: visible !important; opacity: 1 !important; visibility: visible !important;
                            }
                            .ns-tag-group[data-collapsed="true"] .nav-bar-paths-under-tag{
                              display: block !important; height: 0 !important; max-height: 0 !important;
                              overflow: hidden !important; opacity: 0 !important; visibility: hidden !important;
                            }
                          `
                        } catch {
                        }

                        // Visually hide the developer prefix for tags under the authored Developer group
                        try {
                          const devPrefix = developerPrefix || '[Developer]'
                          const esc = devPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                          const stripRe = new RegExp('^\\s*' + esc + '\\s*')
                          // Find which group is the Developer group by checking which group contains any dev-prefixed tag
                          const devGroups = new Set<string>()
                          Object.entries(groupMap2).forEach(([tag, grp]) => {
                            if (String(tag).startsWith(devPrefix)) devGroups.add(String(grp))
                          })
                          const devGroupName = Array.from(devGroups)[0]
                          if (devGroupName) {
                            desired.forEach((blk) => {
                              const h = blk.querySelector('[data-tag]') as HTMLElement | null
                              if (!h) return
                              const rawAttr = h.getAttribute('data-tag') || ''
                              // Only alter the visible label for headers that belong to the developer group
                              const nm = clean2((rawAttr || h.textContent || '').trim())
                              const grp = normGroupMap2.get(nm)
                              if (grp === devGroupName) {
                                const current = (h.textContent || '').trim()
                                if (stripRe.test(current)) {
                                  const without = current.replace(stripRe, '')
                                  if (without) h.textContent = without
                                }
                              }
                            })
                          }
                        } catch {
                        }

                        // 3) Active tag highlighting based on the current hash / selection
                        try {
                          const scroll = (navRoot.querySelector('nav[part="section-navbar-scroll"]') as HTMLElement) || navRoot
                          const allHeaders = Array.from(scroll.querySelectorAll('.nav-bar-tag-and-paths .ns-tag-header')) as HTMLElement[]
                          const findActiveBlock = () => {
                            const hash = (location.hash || '').trim()
                            if (hash) {
                              // Try the exact href match first
                              const link = scroll.querySelector(`a[href="${hash}"]`) as HTMLAnchorElement | null
                              if (link) return link.closest('.nav-bar-tag-and-paths') as HTMLElement | null
                              // Try relaxed endsWith match
                              const links = Array.from(scroll.querySelectorAll('a[href^="#"]')) as HTMLAnchorElement[]
                              const candidate = links.find((a) => a.getAttribute('href')?.toLowerCase().endsWith(hash.toLowerCase()))
                              if (candidate) return candidate.closest('.nav-bar-tag-and-paths') as HTMLElement | null
                            }
                            // Fallback: if a content section with the hash id exists, try to locate the corresponding nav link by id
                            try {
                              const id = hash.replace(/^#/, '')
                              if (id) {
                                const link = Array.from(scroll.querySelectorAll('a[href^="#"]'))
                                  .map((a) => a as HTMLAnchorElement)
                                  .find((a) => (a.getAttribute('href') || '').replace(/^#/, '') === id)
                                if (link) return link.closest('.nav-bar-tag-and-paths') as HTMLElement | null
                              }
                            } catch {
                            }
                            return null
                          }

                          const applyActive = () => {
                            const activeBlk = findActiveBlock()
                            allHeaders.forEach((h) => h.classList.remove('ns-active-tag'))
                            if (activeBlk) {
                              const h = activeBlk.querySelector('.ns-tag-header') as HTMLElement | null
                              if (h) h.classList.add('ns-active-tag')
                            }
                          }

                          // Initially apply
                          applyActive()
                          // React to hash changes
                          window.addEventListener('hashchange', applyActive)
                          // React to clicks inside shadow nav
                          root.addEventListener('click', (e) => {
                            const t = e.target as HTMLElement | null
                            if (!t) return
                            if (t.closest('a[href^="#"]')) {
                              setTimeout(applyActive, 0)
                            }
                          }, true)
                          // React to nav mutations (e.g., RapiDoc re-render)
                          const mo = new MutationObserver(() => setTimeout(applyActive, 0))
                          mo.observe(scroll, { childList: true, subtree: true, attributes: true })
                          ;(navRoot as any).__nsActiveMo = mo
                        } catch {
                        }
                      }
                    } catch {
                    }

                    // Ensure delegated handlers are present so headers can be opened
                    ensureDelegatedToggleHandlers()
                    // Mark as processed for this nav
                    navRoot.setAttribute('data-ns-collapsible', '1')
                    return true
                  }

                  // Find the best list container inside navRoot that lists headers and operations
                  const allContainers = Array.from(navRoot.querySelectorAll('*')) as HTMLElement[]
                  const scoreContainer = (el: HTMLElement) => {
                    const kids = Array.from(el.children) as HTMLElement[]
                    if (kids.length < 6) return 0
                    let opLike = 0
                    let headerLike = 0
                    kids.forEach((k) => {
                      const txt = (k.textContent || '').trim()
                      if (!txt) return
                      if (txt.startsWith('/')) opLike++
                      // Tag headers are often Title Case and short (no slashes)
                      else if (/^[A-Z].{0,40}$/.test(txt) && !txt.includes('/') && txt.split(' ').length <= 3) headerLike++
                    })
                    // Score favors mixes of both kinds
                    return opLike >= 3 && headerLike >= 1 ? opLike + headerLike : 0
                  }
                  let best: HTMLElement | undefined
                  let bestScore = 0
                  for (const c of allContainers) {
                    const s = scoreContainer(c)
                    if (s > bestScore) {
                      best = c
                      bestScore = s
                    }
                  }
                  if (debug) console.log('RapiDocEx: heuristic list container score =', bestScore, best)
                  if (best && bestScore > 0) {
                    // Build synthetic headers by walking children and marking header-like items
                    const kids = Array.from(best.children) as HTMLElement[]
                    const syntheticHeaders: HTMLElement[] = []
                    kids.forEach((k) => {
                      const txt = (k.textContent || '').trim()
                      if (txt && !txt.startsWith('/') && !k.hasAttribute('data-tag')) {
                        // Treat as a header
                        k.setAttribute('data-tag', txt)
                        syntheticHeaders.push(k)
                      }
                    })
                    if (debug) console.log('RapiDocEx: synthetic headers created =', syntheticHeaders.length)
                  }
                }
                // Re-query both real and synthetic headers
                const headers = Array.from(navRoot.querySelectorAll('[data-tag]')) as HTMLElement[]
                if (headers.length === 0) return false

                // Inject minimal CSS for our groups (scoped to shadow DOM)
                if (!root.getElementById('ns-rapidoc-collapsible-css')) {
                  const styleEl = document.createElement('style')
                  styleEl.id = 'ns-rapidoc-collapsible-css'
                  styleEl.textContent = `
                    .ns-tag-group[data-collapsed="true"] { display: none; }
                    .ns-tag-header { cursor: pointer; user-select: none; }
                    /* Match docs sidebar section heading styling */
                    .ns-tag-separator { pointer-events: none; font-weight: 700; font-size: 19px; line-height: 1.25; padding: 4px 8px 0; margin: 16px 0 8px; color: var(--text); border-top: 1px solid var(--border); opacity: 1; }
                    /* Ensure inner RapiDoc containers are visible when expanded, regardless of inline styles */
                    .ns-tag-group[data-collapsed="false"] .nav-bar-paths-under-tag {
                      display: block !important;
                      height: auto !important;
                      max-height: none !important;
                      overflow: visible !important;
                      opacity: 1 !important;
                      visibility: visible !important;
                    }
                    .ns-tag-group[data-collapsed="true"] .nav-bar-paths-under-tag {
                      display: block !important;
                      height: 0 !important;
                      max-height: 0 !important;
                      overflow: hidden !important;
                      opacity: 0 !important;
                      visibility: hidden !important;
                    }
                  `
                  root.appendChild(styleEl)
                }

                headers.forEach((headerEl) => {
                  // Avoid reprocessing
                  if (!headerEl.classList.contains('ns-tag-header')) headerEl.classList.add('ns-tag-header')
                  // Accessibility and focus
                  headerEl.setAttribute('role', 'button')
                  if (!headerEl.hasAttribute('tabindex')) headerEl.setAttribute('tabindex', '0')

                  const group = document.createElement('div')
                  group.className = 'ns-tag-group'
                  group.setAttribute('data-collapsed', 'true')

                  // Move the following siblings (endpoints) into the group until the next tag header
                  let sib = headerEl.nextElementSibling
                  const toMove: Element[] = []
                  while (sib && !(sib as HTMLElement).hasAttribute('data-tag')) {
                    toMove.push(sib)
                    sib = (sib as Element).nextElementSibling
                  }
                  if (toMove.length > 0) {
                    // Insert the group after the header and move nodes into it
                    headerEl.parentElement?.insertBefore(group, headerEl.nextSibling)
                    toMove.forEach((n) => group.appendChild(n))
                  }
                })

                // Ensure delegated handlers are present so headers can be opened
                ensureDelegatedToggleHandlers()
                // Mark as processed for this nav
                navRoot.setAttribute('data-ns-collapsible', '1')

                // Insert non-clickable group separators based on x-tagGroups metadata
                const hostEl = el as any
                const groupMap: Record<string, string> | undefined = hostEl.__nsTagGroupMap || hostEl.spec?.['x-ns-tagGroupMap']
                const groupOrder: string[] | undefined = hostEl.__nsTagGroupOrder || hostEl.spec?.['x-ns-tagGroupOrder']
                if (groupMap && groupOrder) {
                  // Normalizer used during grouping; must match pre-transform cleaning rules enough to map headers
                  const clean = (s: string) => {
                    try {
                      let v = String(s || '')
                      // Strip any bracketed prefixes like [Developer]
                      v = v.replace(/\[[^\]]*]/g, ' ')
                      // Lowercase afterward for consistent matching
                      v = v.toLowerCase()
                      // strip counts like "(12)" or "- 12" at the end
                      v = v.replace(/\s*[(-]\s*\d+\s*[)]?\s*$/g, '')
                      // remove non-word separators except spaces and dashes
                      v = v.replace(/[^a-z0-9\-\s]+/g, '')
                      // collapse whitespace and dashes
                      v = v.replace(/\s+/g, ' ').trim()
                      return v
                    } catch {
                      return s
                    }
                  }

                  // Ensure minimal CSS so separators are visible in this variant too
                  if (!root.getElementById('ns-rapidoc-collapsible-css')) {
                    const styleEl = document.createElement('style')
                    styleEl.id = 'ns-rapidoc-collapsible-css'
                    styleEl.textContent = `
                      .ns-tag-separator { pointer-events: none; font-weight: 600; padding: 6px 8px; margin: 10px 0 4px; opacity: 0.8; }
                    `
                    root.appendChild(styleEl)
                  }

                  // Build a normalized lookup map for groups (actualTagName -> groupName)
                  const normGroupMap = new Map<string, string>()
                  Object.entries(groupMap).forEach(([tagName, grp]) => {
                    normGroupMap.set(clean(String(tagName)), String(grp))
                  })

                  // Attach observer first so any RapiDoc updates trigger re-insertion
                  if (!(navRoot as any).__nsSepObserver) {
                    let timer: number | undefined
                    const observer = new MutationObserver(() => {
                      if (timer) window.clearTimeout(timer)
                      timer = window.setTimeout(() => {
                        ensureSeparators()
                      }, 50)
                    })
                    observer.observe(navRoot, { childList: true, subtree: true })
                    ;(navRoot as any).__nsSepObserver = observer
                    if (debug) console.log('RapiDocEx: separators observer attached')
                  }

                  const ensureSeparators = () => {
                    // Recompute headers every time in case RapiDoc re-rendered
                    const tagHeaderList = Array.from(navRoot.querySelectorAll('[data-tag]')) as HTMLElement[]
                    const items = tagHeaderList.map((h) => {
                      const rawAttr = h.getAttribute('data-tag')
                      const rawText = (h.textContent || '').trim()
                      const raw = (rawAttr && rawAttr.trim()) || rawText
                      return { el: h, name: raw, norm: clean(raw) }
                    })

                    // Collect existing separators to avoid duplicates
                    const existing = new Set<string>(
                      Array.from(navRoot.querySelectorAll('.ns-tag-separator')).map((n) => (n as HTMLElement).textContent || '')
                    )

                    let insertedCount = 0
                    for (const gName of groupOrder) {
                      if (existing.has(gName)) continue
                      let firstIdx = -1
                      for (let i = 0; i < items.length; i++) {
                        const it = items[i]
                        const grp = normGroupMap.get(it.norm)
                        if (grp === gName) {
                          firstIdx = i;
                          break
                        }
                      }
                      // Fallback: if no exact normalized match, try relaxed by checking any tag names in this group
                      if (firstIdx < 0) {
                        const tagsForGroup = Object.entries(groupMap)
                          .filter(([_, grp]) => grp === gName)
                          .map(([tag]) => clean(tag))
                        for (let i = 0; i < items.length; i++) {
                          const it = items[i]
                          if (tagsForGroup.some((tc) => it.norm === tc || it.norm.includes(tc) || tc.includes(it.norm))) {
                            firstIdx = i
                            break
                          }
                        }
                      }
                      if (firstIdx >= 0) {
                        const sep = document.createElement('div')
                        sep.className = 'ns-tag-separator'
                        sep.setAttribute('aria-hidden', 'true')
                        sep.setAttribute('role', 'presentation')
                        sep.textContent = gName
                        const ref = items[firstIdx].el
                        ref.parentElement?.insertBefore(sep, ref)
                        insertedCount++
                      }
                    }
                    if (debug) console.log('RapiDocEx: separators ensured. Now have =', navRoot.querySelectorAll('.ns-tag-separator').length)
                    navRoot.setAttribute('data-ns-separators', String(navRoot.querySelectorAll('.ns-tag-separator').length))
                    return insertedCount
                  }

                  // Initial insert
                  const initial = ensureSeparators()
                  // Sometimes RapiDoc populates headers a tick later; schedule a follow-up pass
                  if (initial === 0) {
                    requestAnimationFrame(() => {
                      const second = ensureSeparators()
                      if (debug) console.log('RapiDocEx: second ensure pass inserted =', second)
                      if (second === 0) {
                        setTimeout(() => {
                          const third = ensureSeparators()
                          if (debug) console.log('RapiDocEx: third ensure pass inserted =', third)
                        }, 80)
                      }
                    })
                  }
                }

                return true
              }

              // Helper: collapse any nav groups (initial state only)
              const collapseNow = () => {
                if (debug) {
                  const detailsCount = root.querySelectorAll('details').length
                  const navPart = root.querySelectorAll('[part="nav-bar"]').length
                  const ariaCount =
                    ((root.querySelector('[part="nav-bar"]') || root.querySelector('nav'))?.querySelectorAll('[aria-expanded]')
                      .length) || 0
                  const dataTagCount =
                    ((root.querySelector('[part="nav-bar"]') || root.querySelector('nav'))?.querySelectorAll('[data-tag]')
                      .length) || 0
                  console.log(
                    'RapiDocEx: collapseNow counts -> details:',
                    detailsCount,
                    ' nav[part="nav-bar"]:',
                    navPart,
                    ' aria-expanded:',
                    ariaCount,
                    ' data-tag:',
                    dataTagCount
                  )
                }
                // 1) Details-based groups
                const detailSelectors = [
                  'nav details', // common
                  '[part="nav-bar"] details', // part-based
                  'rapi-nav details', // internal element name
                ]
                for (const sel of detailSelectors) {
                  root.querySelectorAll(sel).forEach((d) => {
                    try {
                      (d as HTMLDetailsElement).open = false
                    } catch {
                    }
                  })
                }

                // 3) Ensure HTTP methods in the left nav render as colored badges even if
                //    the current RapiDoc build doesn't annotate them with a class/part.
                //    Heuristic: detect leading verb text (GET/POST/...) on nav items and
                //    inject a span with data-http-method that our CSS styles as a pill.
                try {
                  // noinspection CssInvalidHtmlTagReference
                  const navRoot =
                    (root.querySelector('[part="nav-bar"]') as HTMLElement) ||
                    (root.querySelector('rapi-nav') as HTMLElement) ||
                    (root.querySelector('nav[role="navigation"]') as HTMLElement) ||
                    (root.querySelector('nav') as HTMLElement) ||
                    undefined

                  if (navRoot) {
                    const VERBS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS', 'TRACE']
                    const verbRe = new RegExp('^\\s*(' + VERBS.join('|') + ')\\b')

                    const markBadges = (scope: ParentNode) => {
                      const candidates = Array.from(
                        scope.querySelectorAll(
                          // Focus on likely nav items first; keep broad enough for variant builds
                          '.nav-bar-paths-under-tag *,' +
                          '.nav-bar-tag-and-paths *,' +
                          '[class*="nav"], [class*="path"], [part*="path"], [part*="nav"]'
                        )
                      ) as HTMLElement[]

                      for (const el of candidates) {
                        // Skip if this element already contains a recognized method marker
                        if (el.querySelector('[data-http-method], [part="http-method"], .http-verb')) continue

                        // Only elements with some textual content
                        const raw = (el.textContent || '').trim()
                        if (!raw) continue
                        const m = raw.match(verbRe)
                        if (!m) continue

                        // Try to only modify when the verb is at the very start of the first text node
                        const first = el.firstChild
                        if (!first || first.nodeType !== Node.TEXT_NODE) continue
                        const txt = (first as Text).data
                        const m2 = txt.match(verbRe)
                        if (!m2) continue
                        const method = m2[1]

                        // Avoid double-processing
                        if ((el as any).__nsMethodBadgeApplied) continue

                        // Create a badge span
                        const badge = document.createElement('span')
                        badge.setAttribute('data-http-method', method)
                        badge.setAttribute('part', 'http-method')
                        badge.className = 'http-verb ' + method;
                        badge.textContent = method;

                        // Insert before the remaining text and trim the original text node
                        (first as Text).data = txt.replace(verbRe, '').replace(/^\s+/, '')
                        el.insertBefore(badge, first)

                        ;(el as any).__nsMethodBadgeApplied = true
                      }
                    }

                    // Initial pass
                    markBadges(navRoot)

                    // Observe for further changes to the nav (e.g., expand/collapse)
                    const obs = new MutationObserver((mut) => {
                      for (const r of mut) {
                        if (r.addedNodes && r.addedNodes.length) {
                          r.addedNodes.forEach((n) => {
                            if ((n as Element)?.nodeType === Node.ELEMENT_NODE) markBadges(n as ParentNode)
                          })
                        }
                      }
                    })
                    obs.observe(navRoot, { childList: true, subtree: true })

                    // Store to allow potential cleanup on re-mount
                    ;(el as any).__nsMethodBadgeObserver = obs
                  }
                } catch {
                }

                // 2) Aria-expanded based groups (RapiDoc variants that render buttons instead of <details>)
                // Find any nav region first to scope the query
                // noinspection CssInvalidHtmlTagReference
                const navRoot =
                  (root.querySelector('[part="nav-bar"]') as HTMLElement) ||
                  (root.querySelector('rapi-nav') as HTMLElement) ||
                  (root.querySelector('nav[role="navigation"]') as HTMLElement) ||
                  (root.querySelector('nav') as HTMLElement) ||
                  undefined

                if (navRoot) {
                  const expandedEls = Array.from(navRoot.querySelectorAll('[aria-expanded="true"], [aria-expanded="True"]'))
                  expandedEls.forEach((el) => {
                    const h = el as HTMLElement
                    const tryClick = (target?: Element | null) => {
                      if (!target) return
                      try {
                        ;(target as HTMLElement).click()
                      } catch {
                      }
                    }
                    // 1) Click the element itself
                    tryClick(h)
                    // 2) Click the closest obvious control element
                    const ctrl = h.closest('button, [role="button"], summary, a, [tabindex]')
                    if (ctrl && ctrl !== h) tryClick(ctrl)
                    // 3) If it controls another element via aria-controls, click that element too
                    const controlsId = h.getAttribute('aria-controls')
                    if (controlsId) {
                      const controlled = root.getElementById(controlsId) || navRoot.querySelector(`#${CSS.escape(controlsId)}`)
                      if (controlled) tryClick(controlled)
                    }
                    // 4) As a fallback, try clicking a first child button-like element
                    const childBtn = h.querySelector('button, [role="button"], summary, a, [tabindex]')
                    if (childBtn) tryClick(childBtn)
                  })
                }

                // 3) If neither pattern exists, build custom collapsible groups
                const hasDetails = root.querySelector('details')
                const hasAria = navRoot?.querySelector('[aria-expanded]')
                if (!hasDetails && !hasAria) {
                  ensureCustomCollapsibleGroups()
                }
              }

              // Initial collapse
              collapseNow()

              // Observe for a short time to catch any async nav reflows, then disconnect
              let userInteracted = false
              const stopObserver = () => observer?.disconnect()
              const onAnyClick = (evt: Event) => {
                // If a user clicks a <summary> inside details, stop auto-collapsing to avoid fighting UX
                const target = evt.target as HTMLElement | null
                if (target && target.closest('summary')) {
                  userInteracted = true
                  stopObserver()
                  root.removeEventListener('click', onAnyClick, true)
                }
              }
              root.addEventListener('click', onAnyClick, true)

              // Throttled observer to avoid mutation storms while we restructure the nav
              let collapseScheduled = false
              let collapseRuns = 1 // already ran once
              const MAX_RUNS = 5
              const scheduleCollapse = () => {
                if (userInteracted || collapseScheduled) return
                collapseScheduled = true
                setTimeout(() => {
                  collapseScheduled = false
                  if (userInteracted) return
                  if (collapseRuns >= MAX_RUNS) {
                    stopObserver()
                    return
                  }
                  collapseRuns++
                  collapseNow()
                }, 120)
              }

              const observer = new MutationObserver(() => {
                scheduleCollapse()
              })
              observer.observe(root, { childList: true, subtree: true })

              // Also schedule a few minimal delayed passes to ensure the initial state is closed
              const delays = [60, 250, 800]
              delays.forEach((ms) =>
                setTimeout(() => {
                  if (userInteracted) return
                  if (collapseRuns >= MAX_RUNS) return
                  collapseRuns++
                  collapseNow()
                }, ms)
              )

              // Auto-clean after 3s to avoid long-running observers/timeouts
              window.setTimeout(() => {
                stopObserver()
                root.removeEventListener('click', onAnyClick, true)
              }, 3000)
            } catch (e) {
              if (debug) console.warn('Failed to collapse tags by default:', e)
            }
          }
        }
        el.addEventListener('spec-loaded', onLoaded, { once: true })

        // Safety timeout: if spec-loaded never fires, switch to fallback
        const timeout = window.setTimeout(() => {
          if (!loaded && !canceled) {
            if (debug) console.warn('RapiDocEx: spec-loaded timeout, switching to fallback mode')
            setFallback(true)
          }
        }, 3000)

        // cleanup timeout if unmounted
        if (!canceled) {
          // attach a tiny cleanup to the element for this effect run
          const cleanup = () => window.clearTimeout(timeout)
          el.addEventListener('spec-loaded', cleanup, { once: true })
        }
      } catch (e) {
        if (debug) console.warn('RapiDocEx: falling back to spec-url due to error:', e)
        setFallback(true)
      }
    })
    return () => {
      canceled = true
    }
    // We intentionally do not include containerStyle to avoid re-runs purely on style object changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specUrl, developerPrefix, collapseTagsByDefault, dark, cssVars.brand, cssVars.panel])

  if (fallback) {
    return (
      <rapi-doc
        spec-url={specUrl}
        render-style="focused"
        sort-tags="false"
        theme={dark ? 'dark' : 'light'}
        show-header="false"
        primary-color={cssVars.brand}
        bg-color={cssVars.surface}
        nav-bg-color={cssVars.panel}
        text-color={cssVars.text}
        header-color={cssVars.text}
        nav-text-color={cssVars.text}
        nav-hover-bg-color={dark ? 'rgba(116, 214, 140, 0.10)' : 'rgba(42, 89, 50, 0.07)'}
        nav-accent-color={cssVars.brand}
        regular-font={FONT_STACK}
        mono-font={MONO_STACK}
        schema-style="table"
        schema-description-expanded="true"
        default-schema-tab="schema"
        allow-try="true"
        allow-authentication="true"
        allow-server-selection="true"
        show-curl-before-try="true"
        response-area-height="360px"
        nav-item-spacing="relaxed"
        use-path-in-nav-bar="false"
        show-method-in-nav-bar="true"
        // In fallback mode we must allow RapiDoc to fetch the spec URL itself
        allow-spec-url-load="true"
        allow-spec-file-load="false"
        style={containerStyle}
      />
    )
  }

  // Default: render the element and populate it once ready
  return <rapi-doc ref={elRef as any} style={containerStyle} sort-tags="false"/>
}
